import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAuth = () => {
    try {
      const user = authService.getCurrentUser();
      const authenticated = authService.isAuthenticated();
      
      if (authenticated && authService.isTokenExpired()) {
        authService.logout();
        setCurrentUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
  
      setCurrentUser(user);
      setIsAuthenticated(authenticated);
      setLoading(false);
    } catch (error) {
      console.error('Error in checkAuth:', error);
      setCurrentUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const result = await authService.login(credentials);

      // Verificar si requiere cambio de contraseña
      if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
        localStorage.setItem('userInfo', JSON.stringify(result.user));
        setCurrentUser(result.user);
        return result;
      }

      // Actualizar estado inmediatamente SIN llamar a checkAuth
      setCurrentUser(result.user);
      setIsAuthenticated(true);
      setLoading(false);

      return result;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setCurrentUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const changePassword = async (passwordData) => {
    try {
      const result = await authService.changePassword(passwordData);
      
      // Si se recibieron nuevos tokens, actualizar el estado
      if (result.accessToken) {
        const user = authService.getCurrentUser();
        setCurrentUser(user);
        setIsAuthenticated(true);
        setLoading(false);
      }
      
      return result;
    } catch (error) {
      throw error;
    }
  };

  const refreshAuth = async () => {
    try {
      const token = authService.getToken();
      if (token && authService.isTokenExpired()) {
        await logout();
        return false;
      }
      return authService.isAuthenticated();
    } catch (error) {
      console.error('Error refreshing auth:', error);
      await logout();
      return false;
    }
  };

  // Función para verificar y renovar token si es necesario
  const verifyAndRefreshToken = async () => {
    try {
      const token = authService.getToken();
      if (token && authService.isTokenExpired()) {
        await logout();
        return false;
      }
      return authService.isAuthenticated();
    } catch (error) {
      console.error('Error verifying token:', error);
      await logout();
      return false;
    }
  };

  const changePasswordWithChallenge = async (passwordData) => {
    try {
      const result = await authService.changePassword(passwordData);

      // Si el cambio de contraseña fue exitoso, actualizar estado
      if (result.token) {
        setCurrentUser(result.user);
        setIsAuthenticated(true);
        setLoading(false);
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    login,
    logout,
    changePassword,
    refreshAuth,
    checkAuth,
    verifyAndRefreshToken,
    changePasswordWithChallenge,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 